import React, { useEffect, useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import api from '../../utils/api';

interface ChatQuestion {
  id: number;
  question: string;
  userId?: string;
  userName?: string;
  createdAt: string;
}

interface QuestionGroup {
  representativeQuestion: ChatQuestion;
  questions: ChatQuestion[];
  similarity: number;
}

interface Stats {
  totalQuestions: number;
  groupedCount: number;
  ungroupedCount: number;
  groupCount: number;
}

export const ChatQuestionsManagement: React.FC = () => {
  const [groups, setGroups] = useState<QuestionGroup[]>([]);
  const [ungrouped, setUngrouped] = useState<ChatQuestion[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [similarityThreshold, setSimilarityThreshold] = useState<number>(0.75);
  const [minGroupSize, setMinGroupSize] = useState<number>(2);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const fetchGroupedQuestions = async (threshold?: number, groupSize?: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/chat-questions/grouped', {
        params: {
          similarityThreshold: threshold ?? similarityThreshold,
          minGroupSize: groupSize ?? minGroupSize,
        },
      });

      if (response.data.success) {
        setGroups(response.data.data.groups || []);
        setUngrouped(response.data.data.ungrouped || []);
        setStats(response.data.data.stats || null);
      } else {
        setError('Failed to fetch grouped questions');
      }
    } catch (err: any) {
      console.error('Error fetching grouped questions:', err);
      setError(err.response?.data?.error || 'Failed to fetch grouped questions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupedQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    fetchGroupedQuestions();
  };

  const handleGroupToggle = (groupIndex: number) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupIndex)) {
      newExpanded.delete(groupIndex);
    } else {
      newExpanded.add(groupIndex);
    }
    setExpandedGroups(newExpanded);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatSimilarity = (similarity: number) => {
    return `${(similarity * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Chat Questions Summary
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Questions
                </Typography>
                <Typography variant="h4">{stats.totalQuestions}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Grouped Questions
                </Typography>
                <Typography variant="h4">{stats.groupedCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Ungrouped Questions
                </Typography>
                <Typography variant="h4">{stats.ungroupedCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Question Groups
                </Typography>
                <Typography variant="h4">{stats.groupCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filter Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Similarity Threshold</InputLabel>
              <Select
                value={similarityThreshold}
                label="Similarity Threshold"
                onChange={(e) => {
                  const newThreshold = e.target.value as number;
                  setSimilarityThreshold(newThreshold);
                  fetchGroupedQuestions(newThreshold, minGroupSize);
                }}
              >
                <MenuItem value={0.6}>60%</MenuItem>
                <MenuItem value={0.65}>65%</MenuItem>
                <MenuItem value={0.7}>70%</MenuItem>
                <MenuItem value={0.75}>75%</MenuItem>
                <MenuItem value={0.8}>80%</MenuItem>
                <MenuItem value={0.85}>85%</MenuItem>
                <MenuItem value={0.9}>90%</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Min Group Size</InputLabel>
              <Select
                value={minGroupSize}
                label="Min Group Size"
                onChange={(e) => {
                  const newSize = e.target.value as number;
                  setMinGroupSize(newSize);
                  fetchGroupedQuestions(similarityThreshold, newSize);
                }}
              >
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={3}>3</MenuItem>
                <MenuItem value={4}>4</MenuItem>
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Grouped Questions */}
      <Typography variant="h5" sx={{ mb: 2, mt: 4 }}>
        Grouped Questions ({groups.length} groups)
      </Typography>

      {groups.length === 0 ? (
        <Alert severity="info">No question groups found with the current settings.</Alert>
      ) : (
        groups.map((group, index) => (
          <Accordion
            key={index}
            expanded={expandedGroups.has(index)}
            onChange={() => handleGroupToggle(index)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  label={`${group.questions.length} questions`}
                  color="primary"
                  size="small"
                />
                <Typography variant="body1" sx={{ flex: 1 }}>
                  {group.representativeQuestion.question}
                </Typography>
                <Chip
                  label={`Similarity: ${formatSimilarity(group.similarity)}`}
                  size="small"
                  color="secondary"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Question</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.questions.map((question) => (
                      <TableRow key={question.id}>
                        <TableCell>{question.question}</TableCell>
                        <TableCell>
                          {question.userName ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PersonIcon fontSize="small" />
                              {question.userName}
                            </Box>
                          ) : (
                            <Chip label="Anonymous" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarIcon fontSize="small" />
                            {formatDate(question.createdAt)}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Ungrouped Questions */}
      {ungrouped.length > 0 && (
        <>
          <Typography variant="h5" sx={{ mb: 2, mt: 4 }}>
            Ungrouped Questions ({ungrouped.length})
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Question</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ungrouped.slice(0, 50).map((question) => (
                  <TableRow key={question.id}>
                    <TableCell>{question.question}</TableCell>
                    <TableCell>
                      {question.userName ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon fontSize="small" />
                          {question.userName}
                        </Box>
                      ) : (
                        <Chip label="Anonymous" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarIcon fontSize="small" />
                        {formatDate(question.createdAt)}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {ungrouped.length > 50 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Showing first 50 of {ungrouped.length} ungrouped questions
                </Typography>
              </Box>
            )}
          </TableContainer>
        </>
      )}
    </Box>
  );
};

